import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import KTImageInput from 'general/components/OtherKeenComponents/FileUpload/KTImageInput';
import Utils from 'general/utils/Utils';
import AppResource from 'general/constants/AppResource';
import KTFormInput, {
  KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import AppConfigs from 'general/constants/AppConfigs';
import miscHelper from 'general/helpers/miscHelper';
import _ from 'lodash';
import SystemHelper from 'features/System/systemHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import AppConstants from 'general/constants/AppConstants';
import MaintainerHelper from 'features/Maintainer/maintainerHelper';

DialogUpdateAccountView.propTypes = {
  onCancel: PropTypes.func,
  onDone: PropTypes.func,
  user: PropTypes.object,
};

function DialogUpdateAccountView({ onCancel, onDone, user }) {
  // MARK: ---- PROPERTIES ----
  const adminRole =
    user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
  const [loading, setLoading] = useState(false);
  const [readyToValidate, setReadyToValidate] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatar, setAvatar] = useState();
  const [fullname, setFullname] = useState();
  const [phone, setPhone] = useState();
  const [email, setEmail] = useState();
  const [permission, setPermission] = useState();
  useEffect(() => {
    if (user) {
      setAvatar(user.avatar);
      setFullname(user.fullname);
      setPhone(user.phone);
      setEmail(user.email);
      setPermission(user?.permissionGroupId
        ? user?.permissionGroup?.name
        : !adminRole ? `Admin ${user?.agency?.primaryRole}` : '');
    }
  }, [user]);
  const isAgencyUser = useMemo(() => {
    return user?.email === user?.agency?.email
  }, [user])
  const isMaintainer = user?.role === 'MAINTAINER';

  // MARK: ---- FUNCTIONS ----
  const handleSubmit = async () => {
    setReadyToValidate(true);
    if (!Utils.isValid(fullname) || !Utils.isValid(phone) || !Utils.isValid(email)) {
      ToastHelper.showError('Vui lòng điền đầy đủ thông tin');
      return;
    }
    let params = {
      fullname: fullname,
      phone: phone,
      email: email,
    };
    if (avatarFile) {
      const res = await miscHelper.callApiUploadFile(avatarFile);
      if (_.isArray(res) && res.length > 0) {
        params.avatar = res[0].relativePath;
      }
    } else {
      if (!Utils.isValid(avatar)) {
        params.avatar = null;
      }
    }
    setLoading(true);
    if (isMaintainer) {
      const res = await MaintainerHelper.API.requestMaintainerUpdateProfile(params);
      if (_.isBoolean(res) && res) {
        ToastHelper.showSuccess('Cập nhật thông tin tài khoản thành công');
        onDone();
      } else if (_.isString(res)) {
        ToastHelper.showError(res);
      }
    } else {
      const res = await SystemHelper.API.Account.requestUpdateAccountInfo(user?.userId, params);
      let updateAgencySuccess = true;
      if (isAgencyUser) {
        updateAgencySuccess = await DealerHelper.callApiUpdateAgency(
          user?.agency?.agencyId,
          {
            name: fullname,
            phone: phone,
          }
        );
      }
      if (_.isBoolean(res) && res && updateAgencySuccess) {
        ToastHelper.showSuccess('Cập nhật thông tin tài khoản thành công');
        onDone();
      } else if (_.isString(res)) {
        ToastHelper.showError(res);
      }
    }
    setLoading(false);
  };

  return (
    <div>
      <div className="row m-0 p-0 px-2 mt-4">
        {/* avatar */}
        <div className="col-12 mb-4 px-2">
          {UIHelper.buildFormLabel('Ảnh đại diện')}
          <div className="row m-0 p-0">
            <div className="col-lg-3 col-md-4 col-6 p-0">
              <KTImageInput
                fullWidth={true}
                aspectRatio={'1'}
                name="avatar"
                editable={true}
                value={Utils.isValid(avatar) ? Utils.getFullUrl(avatar) : null}
                acceptImageTypes={['image/png', 'image/jpg', 'image/jpeg']}
                defaultImage={AppResource.icons.avatar.default}
                enableCheckValid
                onSelectedFile={(file) => {
                  setAvatarFile(file);
                  const imageUrl = URL.createObjectURL(file);
                  setAvatar(imageUrl);
                }}
                onRemovedFile={() => {
                  setAvatarFile(null);
                  setAvatar('');
                }}
              />
            </div>
            <div className='p-0'>
              Định dạng: JPG, PNG, JPEG
            </div>
          </div>
        </div>
        {/* fullname */}
        <div className="col-md-6 mb-4 px-2">
          {UIHelper.buildFormLabel('Họ và tên', true)}
          <KTFormInput
            type={KTFormInputType.text}
            placeholder="Nhập họ và tên"
            name="fullname"
            value={fullname}
            onChange={(value) => {
              setFullname(value);
            }}
            enableCheckValid={readyToValidate}
            isValid={Utils.isValid(fullname)}
            feedbackText={'Vui lòng nhập họ và tên'}
            isTouched={readyToValidate}
          />
        </div>
        {/* phone */}
        <div className="col-md-6 mb-4 px-2">
          {UIHelper.buildFormLabel('Số điện thoại', true)}
          <KTFormInput
            type={KTFormInputType.telephone}
            placeholder="Nhập số điện thoại"
            name="phone"
            value={phone}
            onChange={(value) => {
              setPhone(value);
            }}
            enableCheckValid={readyToValidate}
            isValid={Utils.isValid(phone) && phone.match(AppConfigs.regex.phone)}
            isTouched={readyToValidate}
            feedbackText={
              !Utils.isValid(phone)
                ? 'Vui lòng nhập số điện thoại'
                : !phone.match(AppConfigs.regex.phone)
                  ? 'Số điện thoại không hợp lệ'
                  : ''
            }
          />
        </div>
        {/* email */}
        <div className="col-md-6 mb-4 px-2">
          {UIHelper.buildFormLabel('Email', true)}
          <KTFormInput
            type={KTFormInputType.email}
            placeholder="Nhập email"
            name="email"
            value={email}
            onChange={(value) => {
              setEmail(value);
            }}
            disabled={true}
            enableCheckValid={readyToValidate}
            isValid={Utils.isValid(email) && email.match(AppConfigs.regex.email)}
            isTouched={readyToValidate}
            feedbackText={
              !Utils.isValid(email)
                ? 'Vui lòng nhập email'
                : !email.match(AppConfigs.regex.email)
                  ? 'Email không hợp lệ'
                  : ''
            }
          />
        </div>
        {/* permission */}
        {user?.role !== AppConstants.userRole.maintainer && (
          <div className="col-md-6 mb-4 px-2">
            {UIHelper.buildFormLabel('Nhóm quyền', !adminRole)}
            <KTFormInput
              type={KTFormInputType.email}
              placeholder="Nhập tên nhóm quyền"
              name="permission"
              value={permission}
              onChange={(value) => {
                setPermission(value);
              }}
              disabled={true}
              enableCheckValid={readyToValidate}
              isValid={adminRole || Utils.isValid(permission)}
              isTouched={readyToValidate}
              feedbackText={
                !adminRole && !Utils.isValid(permission) ? 'Vui lòng nhập tên nhóm quyền' : ''
              }
            />
          </div>
        )}
      </div>
      {/* actions */}
      <div
        className="d-flex align-items-center justify-content-end gap-3 px-4 py-3"
        style={{
          borderTop: `1px solid ${AppColors.gray[200]}`,
        }}
      >
        {UIHelper.buildNegativeButton('Bỏ qua', onCancel, null, false)}
        {UIHelper.buildGreenButton('Cập nhật', handleSubmit, null, false, loading)}
      </div>
    </div>
  );
}

export default DialogUpdateAccountView;
